import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { appConstants } from "utils/appConstants";
import { showResMsg } from "utils/generic";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";

// Selection belongs to the current filtered page, never to hidden records.
export default function useDraftBulkDelete({ enabled, rows, scopeKey, loading,
  endpoint, businessUserID, onDeleted, permanent = false }) {
  const [selection, setSelection] = useState({ scopeKey, ids: [] });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const ids = enabled && selection.scopeKey === scopeKey ? selection.ids : [];
  const availableKey = JSON.stringify(rows.map((row) => String(row._id)));
  const available = useMemo(() => JSON.parse(availableKey), [availableKey]);

  useEffect(() => {
    if (inFlight.current) return;
    setSelection({ scopeKey, ids: [] });
    setOpen(false);
    setError("");
  }, [enabled, scopeKey]); // Data refreshes must not dismiss a failure/retry dialog.

  useEffect(() => {
    if (loading || inFlight.current) return;
    setSelection((current) => {
      const next = current.ids.filter((id) => available.includes(id));
      return next.length === current.ids.length ? current : { ...current, ids: next };
    });
  }, [available, loading, busy]);

  useEffect(() => {
    if (!busy && !ids.length) {
      if (open && error) showResMsg(error, 3500, false);
      setOpen(false);
    }
  }, [busy, ids.length, open, error]);

  const select = (next) => {
    if (!busy && !loading) setSelection({ scopeKey, ids: next });
  };
  const close = () => { if (!inFlight.current) { setOpen(false); setError(""); } };
  const remove = async () => {
    if (inFlight.current || !ids.length || loading) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    const failed = [];
    let reason = "";
    for (const id of ids) {
      try {
        const { data } = await axios.post(`${appConstants.baseURL}${endpoint}`,
          { _id: id, businessUserID, draftOnly: true },
          { headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` }, timeout: 30000 });
        if (!data?.Result_Status) throw new Error(data?.Result_Message || "Unable to delete this draft.");
      } catch (failure) {
        failed.push(id);
        if (!reason) reason = failure.response?.data?.Result_Message || failure.message;
      }
    }
    setSelection({ scopeKey, ids: failed });
    const deleted = ids.length - failed.length;
    if (failed.length) setError(`${deleted ? `${deleted} draft${deleted === 1 ? "" : "s"} deleted. ` : ""}${failed.length} draft${failed.length === 1 ? " was" : "s were"} not deleted. ${reason}`);
    else setOpen(false);
    // Refresh even on failures: a selected record may have been issued elsewhere.
    try { await onDeleted(deleted); }
    catch { setError("Refresh the register to check the latest draft status."); }
    finally {
      inFlight.current = false;
      setBusy(false);
      if (deleted && !failed.length) showDraftSavedMessage(deleted === 1 ? "Draft deleted successfully" : "Drafts deleted successfully");
    }
  };

  return { enabled, ids, open, busy, error, permanent, disabled: busy || loading,
    canSelect: (id) => available.includes(String(id)),
    all: available.length > 0 && available.every((id) => ids.includes(id)),
    some: ids.length > 0, count: ids.length, available: available.length,
    toggle: (id) => { if (available.includes(String(id))) select(ids.includes(String(id)) ? ids.filter((value) => value !== String(id)) : [...ids, String(id)]); },
    toggleAll: () => select(available.every((id) => ids.includes(id)) ? [] : available),
    requestDelete: () => { if (ids.length && !loading) { setError(""); setOpen(true); } },
    close, remove };
}
